import { useEffect, useState } from 'react';
import {
  Button,
  ComboBox,
  DatePicker,
  DatePickerInput,
  NumberInput,
  InlineLoading,
  InlineNotification,
  Modal,
  Layer,
} from '@carbon/react';
import {
  Information,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
} from '@carbon/react/icons';
import { useAtomValue, useSetAtom } from 'jotai';
import { FloatingTooltip } from 'components/floating-tooltip';
import {
  fetchEstablishmentsAtom,
  establishmentsArrayAtom,
  selectedEstablishmentIdsAtom,
  hoveredEstablishmentRefLocationIdAtom,
  updateEstablishmentsMutationAtom,
} from 'atoms/establishments-atoms';
import { useEstablishments } from 'lib/hooks/useEstablishments';
import { TitleChainValidatorFormProps } from './types';
import {
  getCurrentDateString,
  getDateString,
  parseDateString,
} from 'utils/dates';
import type { Establishment } from 'types/api-responses';
import { EstablishmentsTable } from './components/EstablishmentsTable';
import { EstablishmentsMap } from './components/EstablishmentsMap';
import styles from './title-chain-validator-form.module.scss';

// flatpickr only fires onChange for calendar picks — typed or pasted text
// never reaches it — so raw input changes must be parsed separately.
// The full-format guard keeps partial input (e.g. a 2-digit year mid-typing)
// from registering as a valid date; date-fns accepts 1-3 digit years.
const parseMdyInput = (value: string): string | null => {
  const raw = value.trim();
  if (!/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(raw)) return null;
  const parsed = parseDateString(raw, 'M/d/yyyy');
  return parsed ? getDateString(parsed) : null;
};

export const TitleChainValidatorForm = ({
  targetEntityId,
  coverageThreshold,
  cutoffDate,
  isLoading,
  entities,
  entitiesLoading,
  progress,
  hasResult,
  hasError,
  validationTimestamp,
  documentId,
  onTargetEntityIdChange,
  onCoverageThresholdChange,
  onCutoffDateChange,
  onSubmit,
}: TitleChainValidatorFormProps) => {
  const fetchEstablishments = useSetAtom(fetchEstablishmentsAtom);
  const updateEstablishments = useSetAtom(updateEstablishmentsMutationAtom);
  const { deleteEstablishments } = useEstablishments();
  const establishments = useAtomValue(establishmentsArrayAtom);
  const selectedIds = useAtomValue(selectedEstablishmentIdsAtom);
  const setHoveredRefLocationId = useSetAtom(
    hoveredEstablishmentRefLocationIdAtom
  );
  const [isExpanded, setIsExpanded] = useState(true);
  const [isMapVisible, setIsMapVisible] = useState(true);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [confirmVariant, setConfirmVariant] = useState<
    'none-exist' | 'none-selected'
  >('none-selected');

  useEffect(() => {
    if (documentId) {
      fetchEstablishments({ documentId: Number(documentId) });
    }
  }, [documentId, fetchEstablishments]);

  const entityItems = entities.map((party) => {
    const capacity = party.role === 'grantee' ? 'Conveyee' : 'Conveyor';
    return {
      id: party.entity.id.toString(),
      label: `${party.entity.name} (${party.entity.id}) - ${capacity}`,
    };
  });

  const selectedItem = entityItems.find((item) => item.id === targetEntityId);

  const thresholdValue = parseFloat(coverageThreshold);
  const isThresholdInvalid =
    isNaN(thresholdValue) || thresholdValue < 0.1 || thresholdValue > 100;

  const progressPercentage = progress
    ? Math.round((progress.current_step / progress.total_steps) * 100)
    : 0;
  const progressLabel = progress
    ? `${progress.step_label} ...`
    : 'Initializing ...';

  const selectedCount = selectedIds?.length || 0;

  const handleSaveEstablishment = (establishment: Partial<Establishment>) => {
    if (!establishment.referenceLocationId || !establishment.entityId) {
      setSaveError('Please select both a location and an entity');
      return;
    }

    const existingForLocation = establishments
      .filter(
        (e) =>
          e.referenceLocationId === establishment.referenceLocationId &&
          e.id !== establishment.id
      )
      .map((e) => ({
        id: e.id,
        establishment_date: e.establishmentDate,
        entity_id: e.entityId,
        surface: e.surface,
        mineral: e.mineral,
      }));

    const newOrEdited = {
      ...(establishment.id ? { id: establishment.id } : {}),
      establishment_date:
        establishment.establishmentDate || getCurrentDateString(),
      entity_id: establishment.entityId,
      surface: establishment.surface ?? null,
      mineral: establishment.mineral ?? null,
    };

    updateEstablishments({
      url: `/api/title_chains/establishments?reference_location_id=${establishment.referenceLocationId}`,
      type: 'PATCH',
      data: {
        establishments: [...existingForLocation, newOrEdited],
      },
      onSuccess: () => {
        setSaveError(null);
        fetchEstablishments({ documentId: Number(documentId) });
      },
      onError: (error) => {
        const messages =
          error?.errors?.join(', ') || 'Failed to save establishment';
        setSaveError(messages);
      },
    });
  };

  const handleValidateClick = () => {
    const hasEstablishments = establishments.length > 0;
    const hasSelectedEstablishments = selectedIds && selectedIds.length > 0;

    if (!hasEstablishments) {
      setConfirmVariant('none-exist');
      setIsConfirmOpen(true);
    } else if (!hasSelectedEstablishments) {
      setConfirmVariant('none-selected');
      setIsConfirmOpen(true);
    } else {
      onSubmit();
    }
  };

  const handleDeleteEstablishment = async (establishment: Establishment) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete the establishment for ${establishment.entityName} at ${establishment.referenceLocationName}?`
    );

    if (!confirmed) return;

    await deleteEstablishments(
      establishment.referenceLocationId,
      establishment.establishmentDate,
      establishment.entityId
    );
  };
  return (
    <div className={styles.formContainer}>
      <div className={styles.establishmentsSection}>
        <div className={styles.establishmentsHeader}>
          <button
            type="button"
            aria-expanded={isExpanded}
            className={styles.establishmentsHeaderToggle}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? <ChevronDown /> : <ChevronRight />}
            <span className={styles.establishmentsTitle}>
              Establishments
              <span className={styles.establishmentsCounts}>
                {isExpanded
                  ? `(${establishments.length} found · ${selectedCount} selected)`
                  : `(${selectedCount} of ${establishments.length} selected)`}
              </span>
            </span>
          </button>
          {isExpanded && (
            <Button
              kind="ghost"
              size="sm"
              renderIcon={isMapVisible ? ChevronRight : ChevronLeft}
              className={styles.mapToggle}
              onClick={() => setIsMapVisible((prev) => !prev)}
            >
              {isMapVisible ? 'Hide Map' : 'Show Map'}
            </Button>
          )}
        </div>

        {saveError && (
          <InlineNotification
            kind="error"
            title="Save failed"
            subtitle={saveError}
            onCloseButtonClick={() => setSaveError(null)}
          />
        )}

        {isExpanded && (
          <div
            className={
              isMapVisible
                ? styles.establishmentsContent
                : styles.establishmentsContentFullWidth
            }
          >
            <div className={styles.establishmentsTablePanel}>
              <EstablishmentsTable
                onSave={handleSaveEstablishment}
                onDeleteClick={handleDeleteEstablishment}
                onRowHover={setHoveredRefLocationId}
              />
            </div>
            {isMapVisible && (
              <div className={styles.establishmentsMapPanel}>
                <EstablishmentsMap />
              </div>
            )}
          </div>
        )}
      </div>
      <div className={styles.actionRow}>
        <div className={styles.targetEntityInput}>
          <Layer>
            <ComboBox
              id="target-entity-id"
              titleText="Target Entity"
              placeholder={
                entitiesLoading ? 'Loading entities...' : 'Select an entity'
              }
              items={entityItems}
              itemToString={(item) => (item ? item.label : '')}
              selectedItem={selectedItem || null}
              shouldFilterItem={({ item, inputValue }) => {
                if (!inputValue) return true;
                if (selectedItem && inputValue === selectedItem.label)
                  return true;
                const searchValue = inputValue.toLowerCase();
                return item.label.toLowerCase().includes(searchValue);
              }}
              onChange={({ selectedItem }) => {
                if (selectedItem) {
                  onTargetEntityIdChange(selectedItem.id);
                } else {
                  onTargetEntityIdChange('');
                }
              }}
              disabled={entitiesLoading || isLoading}
            />
          </Layer>
        </div>
        <div className={styles.coverageInput}>
          <Layer>
            <NumberInput
              id="coverage-threshold"
              label={
                <span className={styles.thresholdLabel}>
                  Coverage (%)
                  <FloatingTooltip
                    content="Minimum percentage of spatial overlap required to consider a conveyance valid. Lower values allow smaller overlaps to be considered valid."
                    placement="top"
                  >
                    <Information />
                  </FloatingTooltip>
                </span>
              }
              min={0.1}
              max={100}
              step={0.1}
              value={parseFloat(coverageThreshold) || 1}
              onChange={(e: any, state: any) => {
                const value =
                  state?.value ?? (e.target as HTMLInputElement).value;
                onCoverageThresholdChange(String(value));
              }}
              hideSteppers={false}
              allowEmpty={false}
              invalid={isThresholdInvalid}
              invalidText="Must be between 0.1% and 100%"
              disabled={isLoading}
            />
          </Layer>
        </div>
        <div className={styles.cutoffDateInput}>
          <Layer>
            <DatePicker
              datePickerType="single"
              dateFormat="m/d/Y"
              // Date object, not the ISO string — flatpickr can't parse ISO
              // against m/d/Y and clears the input via onChange([])
              value={(cutoffDate && parseDateString(cutoffDate)) || ''}
              onChange={([date]: Date[]) => {
                if (date) {
                  const year = date.getFullYear();
                  const month = String(date.getMonth() + 1).padStart(2, '0');
                  const day = String(date.getDate()).padStart(2, '0');
                  onCutoffDateChange(`${year}-${month}-${day}`);
                } else {
                  onCutoffDateChange('');
                }
              }}
            >
              <DatePickerInput
                id="cutoff-date"
                labelText={
                  <span className={styles.thresholdLabel}>
                    Cutoff Date
                    <FloatingTooltip
                      content="Documents dated after the cutoff date are excluded from the title chain. Leave blank to default to today's date."
                      placement="top"
                    >
                      <Information />
                    </FloatingTooltip>
                  </span>
                }
                placeholder="mm/dd/yyyy"
                disabled={isLoading}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const raw = e.target.value.trim();
                  if (raw === '') {
                    onCutoffDateChange('');
                    return;
                  }
                  const iso = parseMdyInput(raw);
                  if (iso) onCutoffDateChange(iso);
                }}
              />
            </DatePicker>
          </Layer>
        </div>
        {isLoading ? (
          <Button kind="primary" size="md" disabled>
            <InlineLoading description="Validating..." />
          </Button>
        ) : (
          <Button
            kind="primary"
            size="md"
            onClick={handleValidateClick}
            disabled={
              isLoading ||
              !targetEntityId ||
              entitiesLoading ||
              isThresholdInvalid
            }
          >
            Validate Title Chain
          </Button>
        )}

        {validationTimestamp && (
          <div className={styles.statusContainer}>
            {isLoading && (
              <span className={styles.statusText}>
                Validation Started {validationTimestamp}
              </span>
            )}
            {!isLoading && hasResult && (
              <span className={styles.statusText}>
                Validation Completed {validationTimestamp}
              </span>
            )}
            {!isLoading && hasError && (
              <span className={styles.statusText}>
                Validation Failed {validationTimestamp}
              </span>
            )}
          </div>
        )}
      </div>
      {isLoading && (
        <div className={styles.progressContainer}>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
          <p className={styles.progressLabel}>{progressLabel}</p>
        </div>
      )}
      {
        // An empty establishment_ids list — whether nothing exists or nothing
        // is selected — means create_root_ownership is never called. The graph
        // still fills with conveyance nodes, but none of it is rooted in a real
        // owner. Warn rather than block: the runsheet is still useful.
      }
      <Modal
        open={isConfirmOpen}
        modalHeading={
          confirmVariant === 'none-exist'
            ? 'No establishment set for this document'
            : 'Validate without an establishment?'
        }
        primaryButtonText="Validate anyway"
        secondaryButtonText="Cancel"
        onRequestSubmit={() => {
          setIsConfirmOpen(false);
          onSubmit();
        }}
        onRequestClose={() => setIsConfirmOpen(false)}
        className={styles.confirmModal}
      >
        <p className={styles.confirmDescription}>
          {confirmVariant === 'none-exist'
            ? 'This document has no establishment. '
            : 'No establishment is selected. '}
          Ownership can&apos;t be traced without one, so the chain won&apos;t be
          rooted.
        </p>
      </Modal>
    </div>
  );
};
